import React, { useState, useEffect } from 'react';
import { Offcanvas, Collapse } from 'react-bootstrap';
import axios from 'axios';
import { FaShieldAlt, FaUsers, FaRocket, FaInfoCircle, FaChevronDown, FaChevronUp, FaPaperclip } from 'react-icons/fa';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";

const LicenseModal = ({
  showModal,
  handleClose,
  addLicense,
  editLicense,
  selectedLicenseId,
  selectedLicenseData,
}) => {
  const [licenseData, setLicenseData] = useState({
    start_date: '',
    validity: '',
    remarks: '',
    max_create: '',
    max_login: '0',
    po_attachment: null,
  });
  const [errors, setErrors] = useState({});
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [dynamicBreakdown, setDynamicBreakdown] = useState([]);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  useEffect(() => {
    const fetchLicenseTypes = async () => {
      setLoadingTypes(true);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const userInfo = JSON.parse(localStorage.getItem('user-info'));
        const token = userInfo?.token;

        const response = await axios.get(`${backendUrl}/api/license-types`, {
          headers: { 'Authorization': `Bearer ${token}` ,'Tenant-Origin': origin},
          skipLoader: true,
        });
        const dataObj = response.data.Data;
        
        let types = [];
        if (dataObj && dataObj.data) {
            types = dataObj.data;
        } else {
            types = dataObj || [];
        }
        setLicenseTypes(types);
      } catch (error) {
        console.error('Error fetching license types:', error);
      } finally {
        setLoadingTypes(false);
      }
    };
    if (showModal) {
      fetchLicenseTypes();
    }
  }, [showModal]);

  useEffect(() => {
    if (licenseTypes.length > 0) {
        const modulesFields = [];
        const licenseFields = [];
        
        licenseTypes.forEach(type => {
            const isModule = type.type && (type.type.toLowerCase() === 'module' || type.type.toLowerCase() === 'model');
            if (isModule) {
                modulesFields.push(type.name);
            } else {
                licenseFields.push(type.name);
            }
        });

        const breakdown = [];
        if (licenseFields.length > 0) {
            breakdown.push({
                name: 'License Attributes',
                fields: [...new Set(licenseFields)],
                icon: <FaShieldAlt />,
                color: '#6366f1'
            });
        }
        if (modulesFields.length > 0) {
            breakdown.push({
                name: 'Modules',
                fields: [...new Set(modulesFields)],
                icon: <FaRocket />,
                color: '#f59e0b'
            });
        }
        
        setDynamicBreakdown(breakdown);
        
        // Initialize expansion state
        setExpandedGroups(prev => {
            const next = { ...prev };
            breakdown.forEach((b, i) => {
                if (next[b.name] === undefined) next[b.name] = (i === 0);
            });
            if (next['Others'] === undefined) next['Others'] = false;
            return next;
        });
    }
  }, [licenseTypes]);

  useEffect(() => {
    if (selectedLicenseData) {
      const dataToSet = { ...selectedLicenseData };
      if (dataToSet.custom_fields) {
        let customDict = {};
        try {
            customDict = typeof dataToSet.custom_fields === 'string' 
                ? JSON.parse(dataToSet.custom_fields) 
                : dataToSet.custom_fields;
        } catch(e) { console.error(e); }
        Object.assign(dataToSet, customDict);
      }
      setLicenseData({
         ...dataToSet
      });
    } else {
      setLicenseData({
        start_date: '',
        validity: '',
        remarks: '',
        max_create: '',
        max_login: '0',
        po_attachment: null,
      });
    }
  }, [selectedLicenseId, selectedLicenseData, showModal]);

  const allKnownFields = licenseTypes.map(t => t.name);

  const calculateMaxLogin = (data, fields) => {
    return fields.reduce((sum, field) => sum + (Number(data[field]) || 0), 0);
  };
  
  useEffect(() => {
    const newMaxLogin = calculateMaxLogin(licenseData, allKnownFields);
    const newMaxCreate = newMaxLogin * 3;
  
    if (licenseData.max_login !== newMaxLogin || licenseData.max_create !== newMaxCreate) {
        setLicenseData(prevData => ({
            ...prevData,
            max_login: newMaxLogin,
            // max_create: (prevData.max_create && prevData.max_create > 0) ? prevData.max_create : newMaxCreate
                        max_create: newMaxCreate

        }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ...allKnownFields.map(f => licenseData[f]),
    allKnownFields.length
  ]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setLicenseData((prevData) => {
      let updatedData = { ...prevData, [name]: value };
  
      if (name === 'max_create') {
        const maxAllowed = prevData.max_login * 3;
        updatedData.max_create = Math.min(Number(value) || 0, maxAllowed);
      }
  
      return updatedData;
    });

    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: undefined }));
    }
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setLicenseData(prev => ({ ...prev, po_attachment: file }));
    if (errors.po_attachment) {
      setErrors(prevErrors => ({ ...prevErrors, po_attachment: undefined }));
    }
  };
  
  useEffect(() => {
    if (!showModal) {
        setErrors({});
    }
  }, [showModal]);

  const validateForm = () => {
    const newErrors = {};
    if (!licenseData.start_date) newErrors.start_date = 'StartDate is required';
    if (!licenseData.validity) newErrors.validity = 'Validity is required';
    return newErrors;
  };

  const [loading, setLoading] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        setLoading(false);
        return;
    }
    let apierrors;

    const formData = new FormData();
    Object.keys(licenseData).forEach(key => {
      const value = licenseData[key];
      if (value !== null && value !== undefined) {
          // Only append po_attachment if it's an actual file upload
          if (key === 'po_attachment') {
              if (value instanceof File) {
                  formData.append(key, value);
              }
          } else {
              formData.append(key, value);
          }
      }
    });

    if (selectedLicenseData) {
      apierrors = await editLicense(selectedLicenseData.id, formData);
    } else {
      apierrors = await addLicense(formData);
    }
    if (apierrors && Object.keys(apierrors).length > 0) {
      setErrors(apierrors);
    } else {
      setErrors({});
      handleClose();
    }
  };
 
  const formatFieldName = (name) => {
    return name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <>
    <Offcanvas show={showModal} onHide={handleClose} placement='end' style={{width:'900px'}}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>{selectedLicenseData ? 'Edit License' : 'Add License'}</Offcanvas.Title>
      </Offcanvas.Header>
      <hr style={{ margin: 0 }} /><br/>

      <Offcanvas.Body>
        <form>
          {loadingTypes && <p className="text-center text-muted font-size-12">Loading license configurations...</p>}

          <div className="license-type-groups mb-4">
              {/* Render dynamic groups from licenseTypes metadata */}
              {dynamicBreakdown.map((cat) => (
                  <div key={cat.name} className="mb-4">
                      <div 
                          className="d-flex justify-content-between align-items-center mb-3 p-2" 
                          onClick={() => toggleGroup(cat.name)}
                          style={{ cursor: 'pointer', borderBottom: '1px solid #e2e8f0' }}
                      >
                          <h6 className="text-primary text-uppercase fw-bold mb-0" style={{ fontSize: '13px' }}>
                              {cat.icon} <span className="ms-2">{cat.name}</span>
                          </h6>
                          {expandedGroups[cat.name] ? <FaChevronUp size={12} className="text-muted" /> : <FaChevronDown size={12} className="text-muted" />}
                      </div>
                      <Collapse in={expandedGroups[cat.name]}>
                          <div>
                              <div className="row g-3 px-2">
                                  {cat.fields.map(field => (
                                      <div className="col-md-6" key={field}>
                                          <div className="form-group mb-2">
                                            <label className="text-muted small mb-1" style={{fontSize:'12px', fontWeight: 'bold'}}>{formatFieldName(field)}</label>
                                            <input 
                                                style={{ fontSize: '13px', borderRadius: '14px', height: '39px', border: '0.5px solid rgb(232, 233, 237)' }}
                                                type="number"
                                                name={field}
                                                value={licenseData[field] || ''}
                                                onChange={handleChange}
                                                className="form-control"
                                                placeholder="0"
                                            />
                                          </div>
                                      </div>
                                  ))}
                              </div>
                          </div>
                      </Collapse>
                  </div>
              ))}

              {/* Additional / Uncategorized Attributes listed plainly */}
              {(() => {
                  const groupedFields = dynamicBreakdown.flatMap(cat => cat.fields);
                  const otherFields = allKnownFields.filter(f => !groupedFields.includes(f));
                  
                  if (otherFields.length === 0) return null;

                   return (
                       <div key="Others" className="p-3 mt-2" style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px dashed #e2e8f0' }}>
                           <div 
                               className="d-flex justify-content-between align-items-center mb-0" 
                               onClick={() => toggleGroup('Others')}
                               style={{ cursor: 'pointer' }}
                           >
                               <small className="text-muted text-uppercase fw-bold ls-1 font-size-10">License Attributes</small>
                               {expandedGroups['Others'] ? <FaChevronUp size={10} className="text-muted" /> : <FaChevronDown size={10} className="text-muted" />}
                           </div>
                           <Collapse in={expandedGroups['Others']}>
                               <div>
                                   <div className="row g-3 mt-2">
                                       {otherFields.map(field => (
                                           <div className="col-md-6" key={field}>
                                               <div className="form-group mb-2">
                                                 <label className="text-muted small mb-1" style={{fontSize:'12px', fontWeight: 'bold'}}>{formatFieldName(field)}</label>
                                                 <input 
                                                     style={{ fontSize: '13px', borderRadius: '14px', height: '39px', border: '0.5px solid rgb(232, 233, 237)' }}
                                                     type="number"
                                                     name={field}
                                                     value={licenseData[field] || ''}
                                                     onChange={handleChange}
                                                     className="form-control"
                                                     placeholder="0"
                                                 />
                                               </div>
                                           </div>
                                       ))}
                                   </div>
                               </div>
                           </Collapse>
                       </div>
                   );
              })()}
          </div>

          <hr style={{ margin: '15px 0' }} />

          <div className="row g-3">
            <div className="col-md-6">
              <div className="form-group mb-2">
                <label style={{fontSize:'12px', fontWeight: 'bold'}} className="mb-1 text-muted small">Max Login</label>
                <input 
                    style={{ fontSize: '13px', width: '100%', borderRadius: '14px',height: '39px',border: '0.5px solid rgb(232, 233, 237)' }}
                    type="number"
                    name="max_login"
                    value={licenseData.max_login}
                    readOnly
                    className="form-control"
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group mb-2">
                <label style={{fontSize:'12px', fontWeight: 'bold'}} className="mb-1 text-muted small">Max Create</label>
                <input 
                    style={{ fontSize: '13px', width: '100%', borderRadius: '14px',height: '39px',border: '0.5px solid rgb(232, 233, 237)' }}
                    type="number"
                    name="max_create"
                    value={licenseData.max_create}
                    onChange={handleChange}
                    className="form-control"
                />
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group mb-2">
                <label style={{fontSize:'12px', fontWeight: 'bold'}} className="mb-1 text-muted small text-black">Start Date</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        className="w-100"
                        format="DD/MM/YYYY"
                        value={licenseData.start_date ? dayjs(licenseData.start_date) : null}
                        onChange={(newValue) => {
                            handleChange({ target: { name: 'start_date', value: newValue ? newValue.format('YYYY-MM-DD') : '' } });
                        }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                error: !!errors?.start_date,
                                sx: { 
                                    width: '100%', 
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: '14px', 
                                        height: '39px', 
                                        backgroundColor: '#fff',
                                        fontSize: '13px',
                                        '& fieldset': {
                                            border: '0.5px solid rgb(232, 233, 237) !important',
                                            borderRadius: '14px', 
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgb(232, 233, 237) !important',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'rgb(232, 233, 237) !important',
                                            borderWidth: '0.5px !important'
                                        }
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '0 14px',
                                        height: '39px',
                                        fontSize: '13px',
                                        boxSizing: 'border-box'
                                    }
                                }
                            },
                            actionBar: {
                                actions: ['today', 'clear'],
                            }
                        }}
                    />
                </LocalizationProvider>
                {errors.start_date && <div className="text-danger mt-1" style={{ fontSize: '12px' }}>*{errors.start_date}</div>}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group mb-2">
                <label style={{fontSize:'12px', fontWeight: 'bold'}} className="mb-1 text-muted small text-black">Validity</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        className="w-100"
                        format="DD/MM/YYYY"
                        value={licenseData.validity ? dayjs(licenseData.validity) : null}
                        onChange={(newValue) => {
                            handleChange({ target: { name: 'validity', value: newValue ? newValue.format('YYYY-MM-DD') : '' } });
                        }}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                error: !!errors?.validity,
                                sx: { 
                                    width: '100%', 
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: '14px', 
                                        height: '39px', 
                                        backgroundColor: '#fff',
                                        fontSize: '13px',
                                        '& fieldset': {
                                            border: '0.5px solid rgb(232, 233, 237) !important',
                                            borderRadius: '14px', 
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'rgb(232, 233, 237) !important',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'rgb(232, 233, 237) !important',
                                            borderWidth: '0.5px !important'
                                        }
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '0 14px',
                                        height: '39px',
                                        fontSize: '13px',
                                        boxSizing: 'border-box'
                                    }
                                }
                            },
                            actionBar: {
                                actions: ['today', 'clear'],
                            }
                        }}
                    />
                </LocalizationProvider>
                {errors.validity && <div className="text-danger mt-1" style={{ fontSize: '12px' }}>*{errors.validity}</div>}
              </div>
            </div>

            <div className="col-md-12">
              <div className="form-group mb-2">
                <label style={{fontSize:'12px', fontWeight: 'bold'}} className="mb-1 text-muted small">Remarks</label>
                <textarea 
                    style={{ fontSize: '13px', width: '100%', borderRadius: '14px', minHeight: '80px', border: '0.5px solid rgb(232, 233, 237)', padding: '10px' }}
                    name="remarks"
                    value={licenseData.remarks || ''}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Enter remarks..."
                />
              </div>
            </div>

            <div className="col-md-12">
              <div className="form-group mb-2">
                <label style={{fontSize:'12px', fontWeight: 'bold'}} className="mb-1 text-muted small d-flex align-items-center">
                  <FaPaperclip className="me-2 text-primary" size={12} /> PO Attachment
                </label>
                <div 
                    className="p-3 border rounded-4 bg-light bg-opacity-10 d-flex align-items-center justify-content-between"
                    style={{ borderStyle: 'dashed !important', borderColor: 'rgb(209, 213, 219) !important' }}
                >
                  <div className="flex-grow-1 overflow-hidden me-3">
                    {licenseData.po_attachment ? (
                      <div className="d-flex align-items-center text-primary fw-bold font-size-12">
                         <span className="text-truncate">{licenseData.po_attachment.name || 'File Selected'}</span>
                      </div>
                    ) : (
                      <span className="text-muted font-size-12">No file selected (PDF, JPG, PNG up to 10MB)</span>
                    )}
                  </div>
                  <label className="btn btn-sm btn-outline-primary rounded-pill px-3 mb-0 pointer">
                    {licenseData.po_attachment ? 'Change File' : 'Choose File'}
                    <input 
                      type="file" 
                      className="d-none" 
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                    />
                  </label>
                </div>
                {errors.po_attachment && <div className="text-danger mt-1" style={{ fontSize: '11px' }}>*{errors.po_attachment}</div>}
              </div>
            </div>
          </div>
        </form>
      </Offcanvas.Body>
      <div className="p-3 d-flex justify-content-end border-top">
      <button type="button" className="btn btn-secondary me-2" onClick={handleClose}>
          Close
        </button>
      <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </Offcanvas>
    </>
  );
};

export default LicenseModal;
